import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { House, MapPin, Plus, PencilSimple, Trash, Sun, CloudSun, Moon, Ruler, Camera, FileImage, X, DownloadSimple, FileText, Printer, Thermometer, CloudRain, CheckCircle, Warning, Broadcast } from '@phosphor-icons/react';
import { DepartmentNav } from '../components/DepartmentNav';
import { UpgradeGate } from '../components/UpgradeGate';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

const SUN_EXPOSURES = ['Full Sun', 'Partial Shade', 'Full Shade'];

// Zone file upload/display component
const ZoneFiles = ({ zoneId }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState('');
  const fileInputRef = React.useRef(null);
  const { t } = useTranslation(['property']);

  useEffect(() => {
    fetchFiles();
  }, [zoneId]);

  const fetchFiles = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/zones/${zoneId}/files`, { withCredentials: true });
      setFiles(data);
    } catch {}
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large. Max 10MB.'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/api/zones/${zoneId}/files`, formData, { withCredentials: true });
      toast.success('File uploaded');
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePreview = async (file) => {
    try {
      const resp = await axios.get(`${API_URL}/api/zones/files/${file.id}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = URL.createObjectURL(resp.data);
      setPreviewUrl(url);
      setPreviewName(file.original_filename);
    } catch {
      toast.error('Failed to load file');
    }
  };

  const handleDelete = async (fileId) => {
    try {
      await axios.delete(`${API_URL}/api/zones/files/${fileId}`, { withCredentials: true });
      toast.success('File removed');
      fetchFiles();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewName('');
  };

  return (
    <div className="mt-3 pt-3 border-t border-border/50">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t('property:measurementsFiles')}</p>
        <label className="cursor-pointer" data-testid={`zone-upload-${zoneId}`}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
          />
          <span className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors">
            <Camera size={14} />
            {uploading ? t('property:uploading') : t('property:addFile')}
          </span>
        </label>
      </div>

      {files.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {files.map((f) => (
            <div
              key={f.id}
              className="group relative flex items-center gap-1.5 bg-muted/50 rounded-md px-2 py-1.5 text-xs cursor-pointer hover:bg-muted transition-colors"
              onClick={() => handlePreview(f)}
              data-testid={`zone-file-${f.id}`}
            >
              <FileImage size={14} className="text-muted-foreground shrink-0" />
              <span className="truncate max-w-[120px]">{f.original_filename}</span>
              <button
                className="opacity-0 group-hover:opacity-100 ml-1 text-destructive hover:text-destructive/80 transition-opacity"
                onClick={(e) => { e.stopPropagation(); handleDelete(f.id); }}
                data-testid={`zone-file-delete-${f.id}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground/60 italic">
          {t('property:uploadPrompt')}
        </p>
      )}

      {/* Image/PDF Preview Modal */}
      {previewUrl && (
        <Dialog open={!!previewUrl} onOpenChange={closePreview}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="text-base flex items-center gap-2">
                <FileImage size={18} />
                {previewName}
              </DialogTitle>
            </DialogHeader>
            <div className="flex justify-center overflow-auto">
              {previewName.toLowerCase().endsWith('.pdf') ? (
                <iframe 
                  src={previewUrl} 
                  title={previewName}
                  className="w-full h-[70vh] rounded-lg border"
                  style={{ minHeight: '500px' }}
                />
              ) : (
                <img src={previewUrl} alt={previewName} className="max-h-[70vh] rounded-lg object-contain" />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => {
                const a = document.createElement('a');
                a.href = previewUrl;
                a.download = previewName;
                a.click();
              }}>
                <DownloadSimple size={16} className="mr-1" />
                {t('property:download')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.open(previewUrl, '_blank')}>
                <FileText size={16} className="mr-1" />
                {t('property:openInNewTab')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
const GRASS_TYPES = [
  'Kentucky Bluegrass', 'Tall Fescue', 'Perennial Ryegrass', 'Fine Fescue',
  'Bermuda', 'Zoysia', 'St. Augustine', 'Centipede', 'Bahia',
  'Cool-Season Mix', 'Warm-Season Mix'
];

const sunIcon = (exposure) => {
  switch (exposure) {
    case 'Full Sun': return <Sun weight="duotone" size={18} className="text-amber-500" />;
    case 'Partial Shade': return <CloudSun weight="duotone" size={18} className="text-blue-500" />;
    case 'Full Shade': return <Moon weight="duotone" size={18} className="text-indigo-500" />;
    default: return <Sun weight="duotone" size={18} className="text-muted-foreground" />;
  }
};

export const PropertyPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['property', 'common', 'nav', 'dashboard']);
  const [property, setProperty] = useState(null);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  // Property form
  const [showPropertyDialog, setShowPropertyDialog] = useState(false);
  const [propName, setPropName] = useState('');
  const [propAddress, setPropAddress] = useState('');
  const [propCity, setPropCity] = useState('');
  const [propState, setPropState] = useState('');
  const [propZip, setPropZip] = useState('');

  // Zone form
  const [showZoneDialog, setShowZoneDialog] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneName, setZoneName] = useState('');
  const [zoneGrass, setZoneGrass] = useState('');
  const [zoneSun, setZoneSun] = useState('');
  const [zoneOrientation, setZoneOrientation] = useState('');
  const [zoneSqFt, setZoneSqFt] = useState('');
  const [zoneNotes, setZoneNotes] = useState('');

  // Weather source settings
  const [weatherSource, setWeatherSource] = useState('openweather');
  const [pwsStationId, setPwsStationId] = useState('');
  const [pwsApiKey, setPwsApiKey] = useState('');
  const [hasPwsKey, setHasPwsKey] = useState(false);
  const [pwsAvailable, setPwsAvailable] = useState(false);
  const [pwsMessage, setPwsMessage] = useState(null);
  const [testingPws, setTestingPws] = useState(false);
  const [pwsTestResult, setPwsTestResult] = useState(null);
  const [savingWeatherSource, setSavingWeatherSource] = useState(false);

  useEffect(() => {
    fetchProperty();
    fetchWeatherSource();
  }, []);

  const fetchWeatherSource = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/property/weather-source`, { withCredentials: true });
      setWeatherSource(res.data.weather_source || 'openweather');
      setPwsStationId(res.data.pws_station_id || '');
      setHasPwsKey(res.data.has_pws_key || false);
      setPwsAvailable(res.data.pws_available || false);
      setPwsMessage(res.data.pws_message || null);
    } catch {}
  };

  const handleTestPws = async () => {
    if (!pwsStationId.trim()) {
      toast.error('Please enter a Station ID');
      return;
    }
    if (!pwsApiKey.trim() && !hasPwsKey) {
      toast.error('Please enter your Weather Underground API Key');
      return;
    }

    setTestingPws(true);
    setPwsTestResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/weather/test-pws`, {
        weather_source: 'pws',
        pws_station_id: pwsStationId,
        pws_api_key: pwsApiKey || 'existing'
      }, { withCredentials: true });
      
      setPwsTestResult({
        success: true,
        data: res.data
      });
      toast.success(`Connected to ${res.data.station_name || res.data.station_id}!`);
    } catch (error) {
      setPwsTestResult({
        success: false,
        error: error.response?.data?.detail || 'Failed to connect to weather station'
      });
      toast.error(error.response?.data?.detail || 'Failed to connect to weather station');
    } finally {
      setTestingPws(false);
    }
  };

  const handleSaveWeatherSource = async () => {
    setSavingWeatherSource(true);
    try {
      const payload = {
        weather_source: weatherSource,
        pws_station_id: weatherSource === 'pws' ? pwsStationId : null,
        pws_api_key: weatherSource === 'pws' ? (pwsApiKey || null) : null
      };
      
      await axios.put(`${API_URL}/api/property/weather-source`, payload, { withCredentials: true });
      toast.success(weatherSource === 'pws' 
        ? `Weather data now from ${pwsStationId}` 
        : 'Weather data now from OpenWeather');
      fetchWeatherSource();
      setPwsApiKey(''); // Clear API key from state after saving
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save weather settings');
    } finally {
      setSavingWeatherSource(false);
    }
  };

  const fetchProperty = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/property`, { withCredentials: true });
      if (res.data) {
        setProperty(res.data);
        setZones(res.data.zones || []);
      }
    } catch (error) {
      console.error('Error fetching property:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProperty = async (e) => {
    e.preventDefault();

    // Get GPS location
    let lat = user?.latitude || null;
    let lng = user?.longitude || null;

    if (navigator.geolocation && !lat) {
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 })
        );
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch (gpsError) {
        console.warn('GPS location unavailable, proceeding without coordinates:', gpsError.message);
      }
    }

    try {
      const res = await axios.post(`${API_URL}/api/property`, {
        name: propName,
        address: propAddress || null,
        city: propCity || null,
        state: propState || null,
        zip_code: propZip || null,
        latitude: lat,
        longitude: lng
      }, { withCredentials: true });
      setProperty(res.data);
      setZones(res.data.zones || []);
      setShowPropertyDialog(false);
      toast.success('Property created! Now add your zones.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create property');
    }
  };

  const openAddZone = () => {
    setEditingZone(null);
    setZoneName('');
    setZoneGrass('');
    setZoneSun('');
    setZoneOrientation('');
    setZoneSqFt('');
    setZoneNotes('');
    setShowZoneDialog(true);
  };

  const openEditZone = (zone) => {
    setEditingZone(zone);
    setZoneName(zone.name);
    setZoneGrass(zone.grass_type || '');
    setZoneSun(zone.sun_exposure || '');
    setZoneOrientation(zone.orientation || '');
    setZoneSqFt(zone.square_footage ? String(zone.square_footage) : '');
    setZoneNotes(zone.notes || '');
    setShowZoneDialog(true);
  };

  const handleSaveZone = async (e) => {
    e.preventDefault();
    const payload = {
      name: zoneName,
      grass_type: zoneGrass || null,
      sun_exposure: zoneSun || null,
      orientation: zoneOrientation || null,
      square_footage: zoneSqFt ? parseFloat(zoneSqFt) : null,
      notes: zoneNotes || null
    };

    try {
      if (editingZone) {
        await axios.put(`${API_URL}/api/property/zones/${editingZone.id}`, payload, { withCredentials: true });
        toast.success('Zone updated!');
      } else {
        await axios.post(`${API_URL}/api/property/zones`, payload, { withCredentials: true });
        toast.success('Zone added!');
      }
      setShowZoneDialog(false);
      fetchProperty();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save zone');
    }
  };

  const handleDeleteZone = async (zoneId) => {
    if (!window.confirm('Delete this zone? Application history will retain the zone name.')) return;
    try {
      await axios.delete(`${API_URL}/api/property/zones/${zoneId}`, { withCredentials: true });
      toast.success('Zone deleted');
      fetchProperty();
    } catch (error) {
      toast.error('Failed to delete zone');
    }
  };

  const totalSqFt = zones.reduce((sum, z) => sum + (z.square_footage || 0), 0);

  if (loading) return <div className="min-h-screen bg-background"><DepartmentNav /></div>;

  return (
    <div data-testid="property-page" className="min-h-screen bg-background">
      <DepartmentNav />
      <UpgradeGate feature="zone_management" featureLabel="Property & Zone Management">
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 data-testid="property-page-title" className="text-3xl font-bold flex items-center gap-2">
              <House weight="duotone" size={32} className="text-primary" />
              {t('property:pageTitle')}
            </h2>
            <p className="text-muted-foreground">{t('property:pageDescription')}</p>
          </div>
        </div>

        {/* Property Card */}
        {!property ? (
          <Card className="border-dashed border-2">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <House weight="duotone" size={64} className="text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground mb-4">{t('property:setupPrompt')}</p>
              <Button data-testid="setup-property-btn" onClick={() => setShowPropertyDialog(true)} size="lg">
                <MapPin weight="bold" size={20} className="mr-2" />
                {t('property:setupButton')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>{property.name}</CardTitle>
                  <CardDescription>
                    {[property.address, property.city, property.state, property.zip_code].filter(Boolean).join(', ') || t('property:locationViaGPS')}
                  </CardDescription>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                  {property.latitude && (
                    <p className="flex items-center gap-1">
                      <MapPin size={14} />
                      {property.latitude.toFixed(4)}, {property.longitude.toFixed(4)}
                    </p>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-3xl font-bold text-primary">{zones.length}</p>
                  <p className="text-sm text-muted-foreground">{t('property:zones')}</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-primary">{totalSqFt > 0 ? totalSqFt.toLocaleString() : '—'}</p>
                  <p className="text-sm text-muted-foreground">{t('property:totalSqFt')}</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-primary">{new Set(zones.map(z => z.grass_type).filter(Boolean)).size || '—'}</p>
                  <p className="text-sm text-muted-foreground">{t('property:grassTypes')}</p>
                </div>
              </div>
              
              {/* Contractor Sheet Button */}
              {zones.length > 0 && totalSqFt > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    onClick={() => navigate('/contractor-sheet')}
                    data-testid="contractor-sheet-btn"
                  >
                    <Printer weight="bold" size={18} className="mr-2" />
                    {t('property:printContractorSheet')}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    {t('property:shareWithLandscapers')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Zones Section */}
        {property && (
          <>
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold">{t('property:lawnZones')}</h3>
              <Button data-testid="add-zone-btn" onClick={openAddZone} size="sm">
                <Plus weight="bold" size={16} className="mr-1" />
                {t('property:addZone')}
              </Button>
            </div>

            {zones.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {zones.map((zone) => (
                  <Card key={zone.id} data-testid={`zone-card-${zone.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          {sunIcon(zone.sun_exposure)}
                          <CardTitle className="text-lg">{zone.name}</CardTitle>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditZone(zone)}>
                            <PencilSimple size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteZone(zone.id)} className="text-destructive hover:text-destructive">
                            <Trash size={16} />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {zone.grass_type && (
                          <div>
                            <p className="text-muted-foreground">{t('property:grassType')}</p>
                            <p className="font-medium">{zone.grass_type}</p>
                          </div>
                        )}
                        {zone.sun_exposure && (
                          <div>
                            <p className="text-muted-foreground">{t('property:sunExposure')}</p>
                            <p className="font-medium">{zone.sun_exposure}</p>
                          </div>
                        )}
                        {zone.orientation && (
                          <div>
                            <p className="text-muted-foreground">Orientation</p>
                            <p className="font-medium">{zone.orientation}-facing</p>
                          </div>
                        )}
                        {zone.square_footage && (
                          <div>
                            <p className="text-muted-foreground">{t('property:area')}</p>
                            <p className="font-medium">{zone.square_footage.toLocaleString()} {t('property:sqft')}</p>
                          </div>
                        )}
                        {zone.notes && (
                          <div className="col-span-2">
                            <p className="text-muted-foreground">{t('property:notes')}</p>
                            <p className="font-medium">{zone.notes}</p>
                          </div>
                        )}
                      </div>
                      <ZoneFiles zoneId={zone.id} />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed border-2">
                <CardContent className="text-center py-8">
                  <Ruler weight="duotone" size={48} className="mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">{t('property:noZonesYet')}</p>
                </CardContent>
              </Card>
            )}

            {/* Quick Zone Templates */}
            {zones.length === 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{t('property:quickStart')}</CardTitle>
                  <CardDescription>{t('property:quickStartDesc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { name: 'Front Yard', sun: 'Full Sun', icon: '🏡' },
                      { name: 'Back Yard', sun: 'Partial Shade', icon: '🌳' },
                      { name: 'Left Side', sun: 'Partial Shade', icon: '⬅️' },
                      { name: 'Right Side', sun: 'Full Shade', icon: '➡️' }
                    ].map((template) => (
                      <Button
                        key={template.name}
                        variant="outline"
                        className="h-auto py-4 flex flex-col gap-1"
                        data-testid={`quick-zone-${template.name.toLowerCase().replace(' ', '-')}`}
                        onClick={async () => {
                          try {
                            await axios.post(`${API_URL}/api/property/zones`, {
                              name: template.name,
                              sun_exposure: template.sun,
                              grass_type: null,
                              square_footage: null,
                              notes: null
                            }, { withCredentials: true });
                            toast.success(`${template.name} added!`);
                            fetchProperty();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to add zone');
                          }
                        }}
                      >
                        <span className="text-2xl">{template.icon}</span>
                        <span className="text-sm font-medium">{template.name}</span>
                        <span className="text-xs text-muted-foreground">{template.sun}</span>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Weather Station Settings */}
        {property && (
          <Card data-testid="weather-station-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Broadcast weight="duotone" size={24} className="text-primary" />
                {t('property:weatherDataSource')}
              </CardTitle>
              <CardDescription>
                {pwsAvailable 
                  ? t('property:weatherDescPws')
                  : t('property:weatherDescDefault')
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Premium Feature Gate */}
              {!pwsAvailable && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Broadcast weight="duotone" size={24} className="text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-amber-800 dark:text-amber-200">{t('property:comingSoon')}</h4>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                        {pwsMessage || t('property:comingSoonDesc')}
                      </p>
                      <Button 
                        size="sm" 
                        className="mt-3 bg-amber-600 hover:bg-amber-700"
                        onClick={() => navigate('/profile')}
                        data-testid="upgrade-for-pws-btn"
                      >
                        {t('property:upgradeEnthusiast')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Source Selector - Only show if PWS is available */}
              {pwsAvailable && (
                <>
                  <div className="space-y-2">
                    <Label>{t('property:dataSource')}</Label>
                    <Select value={weatherSource} onValueChange={setWeatherSource}>
                      <SelectTrigger data-testid="weather-source-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="openweather">
                          <div className="flex items-center gap-2">
                            <CloudRain size={16} />
                            <span>{t('property:openweatherOption')}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="pws">
                          <div className="flex items-center gap-2">
                            <Thermometer size={16} />
                            <span>{t('property:pwsOption')}</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* PWS Configuration */}
                  {weatherSource === 'pws' && (
                    <div className="space-y-4 p-4 bg-muted/50 rounded-lg border">
                      <div className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Thermometer size={18} className="shrink-0 mt-0.5" />
                        <p>
                          {t('property:connectToPws')} {' '}
                          {t('property:findStationsAt')} <a href="https://www.wunderground.com/wundermap" target="_blank" rel="noopener noreferrer" className="text-primary underline">wunderground.com/wundermap</a>
                        </p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="pws-station">{t('property:stationId')} *</Label>
                          <Input
                            id="pws-station"
                            placeholder="e.g., KTXROUND582"
                            value={pwsStationId}
                            onChange={(e) => setPwsStationId(e.target.value.toUpperCase())}
                            data-testid="pws-station-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            {t('property:stationIdHelp')}
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="pws-key">
                            {t('property:wuApiKey')} {hasPwsKey ? `(${t('property:saved')})` : '*'}
                          </Label>
                          <Input
                            id="pws-key"
                            type="password"
                        placeholder={hasPwsKey ? '••••••••••••••••' : 'Your Weather Underground API key'}
                        value={pwsApiKey}
                        onChange={(e) => setPwsApiKey(e.target.value)}
                        data-testid="pws-key-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('property:wuApiKeyHelp')} <a href="https://www.wunderground.com/member/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary underline">wunderground.com/member/api-keys</a> ({t('property:requiresPws')})
                      </p>
                    </div>
                  </div>

                  {/* Test Connection Button */}
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleTestPws}
                      disabled={testingPws || !pwsStationId.trim()}
                      data-testid="test-pws-btn"
                    >
                      {testingPws ? t('property:testing') : t('property:testConnection')}
                    </Button>

                    {pwsTestResult && (
                      <div className={`flex items-center gap-2 text-sm ${pwsTestResult.success ? 'text-green-600' : 'text-red-600'}`}>
                        {pwsTestResult.success ? (
                          <>
                            <CheckCircle weight="fill" size={18} />
                            <span>
                              {pwsTestResult.data.current_temp}°F from {pwsTestResult.data.neighborhood || pwsTestResult.data.station_id}
                            </span>
                          </>
                        ) : (
                          <>
                            <Warning weight="fill" size={18} />
                            <span>{pwsTestResult.error}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Current Status - only show if PWS available */}
              <div className="flex items-center justify-between p-3 bg-background rounded-lg border">
                <div className="flex items-center gap-2">
                  {weatherSource === 'pws' && hasPwsKey && pwsAvailable ? (
                    <>
                      <CheckCircle weight="fill" size={20} className="text-green-600" />
                      <span className="text-sm">
                        Using <strong>{pwsStationId || 'Personal Weather Station'}</strong>
                      </span>
                    </>
                  ) : (
                    <>
                      <CloudRain weight="duotone" size={20} className="text-blue-600" />
                      <span className="text-sm">Using <strong>OpenWeather</strong> ({t('property:cityLevelData')})</span>
                    </>
                  )}
                </div>
                {pwsAvailable && (
                  <Button
                    onClick={handleSaveWeatherSource}
                    disabled={savingWeatherSource || (weatherSource === 'pws' && !pwsStationId.trim())}
                    size="sm"
                    data-testid="save-weather-source-btn"
                  >
                    {savingWeatherSource ? t('property:saving') : t('common:save')}
                  </Button>
                )}
              </div>
              </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Property Setup Dialog */}
      <Dialog open={showPropertyDialog} onOpenChange={setShowPropertyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('property:setupButton')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProperty} className="space-y-4">
            <div>
              <Label>{t('property:propertyName')} *</Label>
              <Input data-testid="prop-name" value={propName} onChange={(e) => setPropName(e.target.value)} placeholder="e.g., My Home" required />
            </div>
            <div>
              <Label>{t('property:streetAddress')}</Label>
              <Input data-testid="prop-address" value={propAddress} onChange={(e) => setPropAddress(e.target.value)} placeholder="123 Main St" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>{t('property:city')}</Label>
                <Input data-testid="prop-city" value={propCity} onChange={(e) => setPropCity(e.target.value)} placeholder="Round Rock" />
              </div>
              <div>
                <Label>{t('property:state')}</Label>
                <Input data-testid="prop-state" value={propState} onChange={(e) => setPropState(e.target.value)} placeholder="TX" />
              </div>
              <div>
                <Label>Zip</Label>
                <Input data-testid="prop-zip" value={propZip} onChange={(e) => setPropZip(e.target.value)} placeholder="78664" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('property:gpsNote')}
            </p>
            <Button data-testid="prop-submit" type="submit" className="w-full">
              {t('property:createProperty')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Zone Dialog */}
      <Dialog open={showZoneDialog} onOpenChange={setShowZoneDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingZone ? t('property:editZone') : t('property:addZone')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveZone} className="space-y-4">
            <div>
              <Label>{t('property:zoneName')} *</Label>
              <Input data-testid="zone-name" value={zoneName} onChange={(e) => setZoneName(e.target.value)} placeholder="e.g., Front Yard" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('property:grassType')}</Label>
                <Select value={zoneGrass} onValueChange={setZoneGrass}>
                  <SelectTrigger data-testid="zone-grass">
                    <SelectValue placeholder={t('property:selectGrass')} />
                  </SelectTrigger>
                  <SelectContent>
                    {GRASS_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('property:sunExposure')}</Label>
                <Select value={zoneSun} onValueChange={setZoneSun}>
                  <SelectTrigger data-testid="zone-sun">
                    <SelectValue placeholder={t('property:selectSun')} />
                  </SelectTrigger>
                  <SelectContent>
                    {SUN_EXPOSURES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Orientation</Label>
                <Select value={zoneOrientation} onValueChange={setZoneOrientation}>
                  <SelectTrigger data-testid="zone-orientation">
                    <SelectValue placeholder="Select facing" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="North">North-facing</SelectItem>
                    <SelectItem value="South">South-facing</SelectItem>
                    <SelectItem value="East">East-facing</SelectItem>
                    <SelectItem value="West">West-facing</SelectItem>
                    <SelectItem value="Mixed">Mixed</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">North = more shade/moisture, South = more sun/heat</p>
              </div>
              <div>
                <Label>{t('property:squareFootage')}</Label>
                <Input data-testid="zone-sqft" type="number" value={zoneSqFt} onChange={(e) => setZoneSqFt(e.target.value)} placeholder="e.g., 2500" />
              </div>
            </div>
            <div>
              <Label>{t('property:notes')}</Label>
              <Input data-testid="zone-notes" value={zoneNotes} onChange={(e) => setZoneNotes(e.target.value)} placeholder="e.g., North-facing, stays damp" />
            </div>
            <Button data-testid="zone-submit" type="submit" className="w-full">
              {editingZone ? t('property:updateZone') : t('property:addZone')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      </UpgradeGate>
    </div>
  );
};
